// src/components/CommonComponents/ChatHeads/VerifyDataModal.jsx
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Trash2, Eye, Upload } from "lucide-react";
import useLocalization from "../../../../../hooks/useLocalization";
import { useLogger } from "../../../../../utils/UnifiedLogger";
import { KpmgTransliterate } from "../../../TranslitrationSuggestion/KpmgTransliterate";
import { useAppContext } from "../../../../../context/AppContext";
import { getClientIP } from "../../../../../utils/util";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import {
  API_ENDPOINTS,
  ORGANISATION_CATEGORY_CODES,
} from "../../../../../utils/constants";
import ApiSearchDropdown from "../BotDynamicForm/ApiSearchDropdown";
import { useDispatch, useSelector } from "react-redux";
import { setIsGrievanceRegistering } from "../../../../../store/features/ui/uiSlice";
import { selectIsGrievanceRegistering } from "../../../../../store/features/ui/uiSelectors";
// import { useFuzzySearch } from "../../../../../hooks/useFuzzySearch";
import Fuse from "fuse.js";
import {
  selectAccumulatedFinalTranscript,
  selectCurrentInterimTranscript,
  selectGrievanceAdditionalComments,
  selectGrievanceFileData,
} from "../../../../../store/features/message/textContentSelectors";
import {
  setGrievanceAdditionalComments,
  setGrievanceFileData,
} from "../../../../../store/features/message/textContentSlice";
import DocumentViewerModal from "../DocumentViewerModal";
import { TextareaAutosize } from "@mui/material";
import SocketMicStreamer from "../../../InputUI/SocketMicStreamerNewUi";

const VerifyDataModal = ({
  handleSubmitGrievance,
  botResponse,
  isOpen,
  closeModal,
  mandatory_data_present,
  updated_grievance = "",
  field_form = {},
  category_code,
  org_code,
  field_form_list,
  serverRemarks,
  grievance_bucket,
  isNotSubcategory = false,
}) => {
  const isOrganisationCategory =
    category_code && ORGANISATION_CATEGORY_CODES.includes(category_code);
  const { logInfo } = useLogger("VERIFY_DATA_MODAL", true);
  const dispatch = useDispatch();
  const isGrievanceRegistering = useSelector(selectIsGrievanceRegistering);
  const accumulatedFinalTranscript = useSelector(
    selectAccumulatedFinalTranscript
  );

  const currentInterimTranscript = useSelector(selectCurrentInterimTranscript);
  const pendingPrefillRef = useRef({});
  const { selectedLanguageData, sharedApiService, userData } = useAppContext();
  const { t } = useLocalization();
  const firstLevelValueRef = useRef("");
  const fileInputRef = useRef(null);
  const fileData = useSelector(selectGrievanceFileData);
  const botAdditionalFieldsRequest = sharedApiService.sendRequest;

  const [isEditOpen, setIsEditOpen] = useState(false);
  //   const [notes, setNotes] = useState("");
  const notes = useSelector(selectGrievanceAdditionalComments);

  const [isNotesSaving, setIsNotesSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const autosaveTimerRef = useRef(null);
  const savedIndicatorTimerRef = useRef(null);

  const [summary, setSummary] = useState(updated_grievance || "");
  const [isSummarySaving, setIsSummarySaving] = useState(false);
  const [isSummarySaved, setIsSummarySaved] = useState(false);

  const summaryTimerRef = useRef(null);
  const summarySavedTimerRef = useRef(null);
  const micRef = useRef(null);
  const currentNotesRef = useRef("");
  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    currentNotesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    const voiceText = [
      accumulatedFinalTranscript?.trim(),
      currentInterimTranscript?.trim(),
    ]
      .filter(Boolean)
      .join(" ");

    if (!voiceText) return;

    // 🚨 prevent overwrite loop
    if (currentNotesRef.current === voiceText) return;

    dispatch(setGrievanceAdditionalComments(voiceText));
  }, [accumulatedFinalTranscript, currentInterimTranscript]);

  useEffect(() => {
    return () => {
      if (summaryTimerRef.current) clearTimeout(summaryTimerRef.current);
      if (summarySavedTimerRef.current)
        clearTimeout(summarySavedTimerRef.current);
    };
  }, []);

  // ✅ State management
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [unmatchedUserInputs, setUnmatchedUserInputs] = useState({}); // Track unmatched inputs
  const [isEditing, setIsEditing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isGrievanceExpanded, setIsGrievanceExpanded] = useState(false);

  // ✅ Field form state
  const [fieldForm, setFieldForm] = useState({ InputFields: [], DropDown: [] });
  const [globalLoading, setGlobalLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isInitialPrefillComplete, setIsInitialPrefillComplete] =
    useState(false);

  // ✅ Dropdown states (like BotDynamicForm)
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [shakingFields, setShakingFields] = useState({});

  // ✅ Refs
  const userInfoSectionRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const dropdownRefs = useRef({});
  const portalRefs = useRef({});
  const errorRefs = useRef({});

  // Fuzzy search for user matched input
  // const [fuzzySearches, setFuzzySearches] = useState({});
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  // console.log('botResponse', botResponse);

  // const grievance_bucket = botResponse?.grievance_bucket || {};
  // const grievance_bucket = botResponse?.grievance_bucket || grievanceBucket || {};

  const handleSummaryChange = (text) => {
    setSummary(text);
    setIsSummarySaved(false);
    setIsSummarySaving(true);

    if (summaryTimerRef.current) {
      clearTimeout(summaryTimerRef.current);
    }

    if (summarySavedTimerRef.current) {
      clearTimeout(summarySavedTimerRef.current);
    }

    summaryTimerRef.current = setTimeout(() => {
      // 👉 Place your API / dispatch update here
      // Example:
      // dispatch(updateSummary(text));

      setIsSummarySaving(false);
      setIsSummarySaved(true);

      summarySavedTimerRef.current = setTimeout(() => {
        setIsSummarySaved(false);
      }, 2000);
    }, 800);
  };

  // 🔹 autosave (debounced)
  const handleNotesChange = (text) => {
    // setNotes(text);
    dispatch(setGrievanceAdditionalComments(text));
    setIsSaved(false);
    setIsNotesSaving(true);

    // Clear previous timers
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    if (savedIndicatorTimerRef.current) {
      clearTimeout(savedIndicatorTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      // 👉 Put your API / Redux save here
      // dispatch(saveNotes(text))
      // console.log("Autosaved notes:", text);

      setIsNotesSaving(false);
      setIsSaved(true);

      // Hide "Saved" after 2 seconds
      savedIndicatorTimerRef.current = setTimeout(() => {
        setIsSaved(false);
      }, 2000);
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (savedIndicatorTimerRef.current)
        clearTimeout(savedIndicatorTimerRef.current);
    };
  }, []);

  // ✅ Close dropdown on click outside (TOP LEVEL useEffect)
  useEffect(() => {
    const handler = (e) => {
      Object.keys(openDropdowns).forEach((fieldName) => {
        if (openDropdowns[fieldName]) {
          const clickedMain = dropdownRefs.current[fieldName]?.contains(
            e.target
          );
          const clickedPortal = portalRefs.current[fieldName]?.contains(
            e.target
          );

          if (!clickedMain && !clickedPortal) {
            setOpenDropdowns((prev) => ({ ...prev, [fieldName]: false }));
          }
        }
      });
    };

    const hasOpenDropdown = Object.values(openDropdowns).some(Boolean);
    if (hasOpenDropdown) {
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [openDropdowns]);

  // ✅ Initialize field form with child fields (like BotDynamicForm)
  useEffect(() => {
    if (!isOpen || !field_form) return;

    let updatedForm = { ...field_form };
    const newInputFields = [...(updatedForm.InputFields || [])];
    const newDropDown = [...(updatedForm.DropDown || [])];

    // Check for parent fields with HasChilds: true but no child element
    newInputFields.forEach((field, idx) => {
      if (field.HasChilds === true) {
        const nextField = newInputFields[idx + 1];
        const isNextFieldChild =
          nextField && nextField.FieldOrder === field.FieldOrder + 1;

        if (!isNextFieldChild) {
          const childField = {
            ...field,
            FieldName: `${field.FieldName}_child`,
            DisplayLable: `Additional Fields`,
            DisplayLable_regional: field.DisplayLable_regional || field.DisplayLable,
            FieldOrder: field.FieldOrder + 1,
            IsMandatory: false,
            options: [],
            HasChilds: false,
            FieldType: "Dropdown",
          };

          newInputFields.splice(idx + 1, 0, childField);
          newDropDown.push([]);
        }
      }
    });

    newInputFields.sort((a, b) => a.FieldOrder - b.FieldOrder);

    updatedForm.InputFields = newInputFields;
    updatedForm.DropDown = newDropDown;

    setFieldForm(updatedForm);
    setIsInitialPrefillComplete(false);
  }, [isOpen, field_form]);

  const dropdownIndexMap = useMemo(() => {
    const map = {};
    let idx = 0;

    fieldForm.InputFields.forEach((f) => {
      if (f.FieldType?.toLowerCase() === "dropdown") {
        map[f.FieldName] = idx;
        idx++;
      }
    });

    return map;
  }, [fieldForm.InputFields]);

  // ✅ Get dropdown index by field name
  // const getDropdownIndexByFieldName = useCallback(
  //   (fieldName) => {
  //     const dropdownFields = fieldForm.InputFields.filter(
  //       (f) => f.FieldType?.toLowerCase() === "dropdown"
  //     );
  //     return dropdownFields.findIndex((f) => f.FieldName === fieldName);
  //   },
  //   [fieldForm.InputFields]
  // );

  // ✅ Get dropdown options for a field
  const getDropdownOptions = useCallback(
    (field) => {
      // const dropdownIndex = getDropdownIndexByFieldName(field.FieldName);
      const dropdownIndex = dropdownIndexMap[field.FieldName];
      const options = fieldForm.DropDown?.[dropdownIndex] || [];
      return options.filter((opt) => opt.Value !== "" || opt.Text !== "");
    },
    [fieldForm.DropDown, dropdownIndexMap]
  );

  // ✅ Call API for child options
  const callAdditionalFieldsAPI = useCallback(
    async (
      fieldObj,
      value,
      isFromPrefill = false,
      prefillChildValue = null,
      prefillChildText = null
    ) => {
      logInfo(
        "CALLING_ADDITIONAL_FIELDS_API",
        "Calling API for additional fields",
        {
          fieldName: fieldObj.FieldName,
          parentValue: value,
          isFromPrefill,
          prefillChildValue,
          prefillChildText,
        }
      );

      setGlobalLoading(true);
      setApiError(null);

      try {
        const sortedFields = [
          ...(fieldForm.InputFields?.filter(
            (f) => f.FieldType?.toLowerCase() === "dropdown"
          ) || []),
        ].sort((a, b) => a.FieldOrder - b.FieldOrder);
        // First field
        const firstLevelField = sortedFields?.[0];

        // Base/initial firstLevelValue logic
        let firstLevelValue = firstLevelValueRef.current;

        if (firstLevelField) {
          if (firstLevelField.FieldType.toLowerCase() === "dropdown") {
            firstLevelValue = formValues[firstLevelField.FieldName];
          }
        }

        if (
          firstLevelValue === undefined ||
          firstLevelValue === null ||
          (firstLevelValue === "" && Object.keys(firstLevelField).length > 0)
        ) {
          firstLevelValue =
            firstLevelField.UserInputValue || firstLevelField.FieldValue || "";
        }

        // If current field is the first field, use parentValue
        if (
          fieldObj.FieldName === firstLevelField?.FieldName &&
          firstLevelField.FieldType?.toLowerCase() === "dropdown"
        ) {
          firstLevelValue = value;
        }

        await botAdditionalFieldsRequest({
          method: "GET",
          url: `${API_ENDPOINTS.getAdditionalDropdownValueList}?language=${selectedLanguageData?.code}&orgCode=${org_code}&firstLevelInputFieldDropdownValue=${firstLevelValue}&parentDropdownFieldName=${fieldObj.FieldName}&parentDropdownValue=${value}&currentCategory=${category_code}`,
          headers: {
            "Content-Type": "application/json",
            Authorization: `CPGRAMS Q0hCT1Q6VEhZR0JINzY4OTBKSFlBV1Na...`,
          },
          abortable: true,
          isEncrypted: false,

          onSuccess: (res) => {
            setGlobalLoading(false);
            console.log("API response for additional fields:", res);
            if (!Array.isArray(res) || res.length === 0) return;

            const formattedOptions = res
              .map((item) => {
                const isEnglish =
                  selectedLanguageData?.code?.toLowerCase() === "en";
                const regionalText =
                  typeof item.Value_regional === "string"
                    ? item.Value_regional.replace(/^\[?'?/, "")
                        .replace(/'?\]?$/, "")
                        .trim()
                    : item.Value_regional;
                const displayText = isEnglish
                  ? item.Value || item.text || item.name || ""
                  : regionalText || item.Value || "";

                const isSelected =
                  prefillChildValue &&
                  (item.Key === prefillChildValue ||
                    item.Value === prefillChildValue);

                return {
                  Value: item.Key || item.Value || "",
                  Text: displayText,
                  Text_regional: regionalText,
                  Disabled: false,
                  Selected: isSelected,
                  isSelected: isSelected,
                };
              })
              .filter((opt) => opt.Value !== "" && opt.Text !== "");

            logInfo(
              "ADDITIONAL_FIELD_OPTIONS_LOADED",
              "Child options loaded from API",
              {
                fieldName: fieldObj.FieldName,
                parentValue: value,
                optionsCount: formattedOptions.length,
              }
            );

            // setFieldForm((prev) => {
            //   const updatedForm = { ...prev };
            //   const dropdownFields = prev.InputFields.filter(
            //     (f) => f.FieldType?.toLowerCase() === "dropdown"
            //   );
            //   const parentDropdownIndex = dropdownFields.findIndex(
            //     (f) => f.FieldName === fieldObj.FieldName
            //   );
            //   const childDropdownIndex = parentDropdownIndex + 1;

            //   // Update DropDown array
            //   if (
            //     updatedForm.DropDown &&
            //     childDropdownIndex < updatedForm.DropDown.length
            //   ) {
            //     updatedForm.DropDown = [...updatedForm.DropDown];
            //     updatedForm.DropDown[childDropdownIndex] = formattedOptions;
            //   }

            //   // Update InputFields
            //   updatedForm.InputFields = updatedForm.InputFields.map(
            //     (f, idx) => {
            //       const fieldDropdownIndex = dropdownFields.findIndex(
            //         (df) => df.FieldName === f.FieldName
            //       );
            //       if (fieldDropdownIndex === childDropdownIndex) {
            //         return {
            //           ...f,
            //           options: formattedOptions,
            //           IsHidden: false,
            //         };
            //       }
            //       return f;
            //     }
            //   );

            //   return updatedForm;
            // });
            setFieldForm((prev) => {
              const updatedForm = { ...prev };

              // Strategy 1: Use parentDropdownIndex + 1 (same as BotDynamicForm)
              const parentIdx = dropdownIndexMap[fieldObj.FieldName];
              const childDropdownIndex = parentIdx !== undefined ? parentIdx + 1 : undefined;

              // Strategy 2: Fallback to synthetic _child field lookup
              const childFieldName = `${fieldObj.FieldName}_child`;
              const syntheticChildIdx = dropdownIndexMap[childFieldName];

              // Use whichever index is valid
              const childIdx = childDropdownIndex !== undefined &&
                updatedForm.DropDown?.[childDropdownIndex] !== undefined
                  ? childDropdownIndex
                  : syntheticChildIdx;

              if (childIdx !== undefined) {
                updatedForm.DropDown = [...updatedForm.DropDown];
                updatedForm.DropDown[childIdx] = formattedOptions;

                // Update the InputFields entry at that dropdown index
                const dropdownFields = updatedForm.InputFields.filter(
                  (f) => f.FieldType?.toLowerCase() === "dropdown"
                );
                const targetFieldName = dropdownFields[childIdx]?.FieldName;

                if (targetFieldName) {
                  updatedForm.InputFields = updatedForm.InputFields.map((f) =>
                    f.FieldName === targetFieldName
                      ? { ...f, options: formattedOptions, IsHidden: false }
                      : f
                  );
                }
              }

              return updatedForm;
            });

            // --- APPLY PENDING PREFILL ONCE OPTIONS ARE LOADED ---
            const pendingValue = pendingPrefillRef.current[fieldObj.FieldName];
            if (pendingValue) {
              const existsInOptions = formattedOptions.some(
                (opt) =>
                  opt.Value?.toString().trim().toLowerCase() ===
                  pendingValue.toString().trim().toLowerCase()
              );

              if (existsInOptions) {
                setFormValues((prev) => ({
                  ...prev,
                  [fieldObj.FieldName]: pendingValue,
                }));

                delete pendingPrefillRef.current[fieldObj.FieldName];

                logInfo(
                  "PREFILL_APPLIED_AFTER_OPTIONS",
                  "Prefilled dropdown value applied after options loaded",
                  {
                    fieldName: fieldObj.FieldName,
                    value: pendingValue,
                  }
                );
              }
            }

            // Handle child value matching after options are loaded
            if (prefillChildValue || prefillChildText) {
              const childFieldName = `${fieldObj.FieldName}_child`;

              // Try to match by Value first
              let matchedOption = formattedOptions.find(
                (opt) =>
                  opt.Value?.toString().trim().toLowerCase() ===
                  prefillChildValue?.toString().trim().toLowerCase()
              );

              // If no match by Value, try by Text
              if (!matchedOption && prefillChildText) {
                matchedOption = formattedOptions.find(
                  (opt) =>
                    opt.Text?.trim().toLowerCase() ===
                      prefillChildText.trim().toLowerCase() ||
                    opt.Text_regional?.trim().toLowerCase() ===
                      prefillChildText.trim().toLowerCase()
                );
              }

              if (matchedOption) {
                // Matched - set the value and clear unmatched
                setFormValues((prevValues) => ({
                  ...prevValues,
                  [childFieldName]: matchedOption.Value,
                }));
                setUnmatchedUserInputs((prev) => {
                  const updated = { ...prev };
                  delete updated[childFieldName];
                  return updated;
                });
                logInfo(
                  "CHILD_VALUE_MATCHED",
                  "Child value matched after API load",
                  {
                    childFieldName,
                    matchedValue: matchedOption.Value,
                  }
                );
              } else {
                // Not matched - keep unmatched text visible
                setFormValues((prevValues) => ({
                  ...prevValues,
                  [childFieldName]: "",
                }));
                if (prefillChildText) {
                  setUnmatchedUserInputs((prev) => ({
                    ...prev,
                    [childFieldName]: prefillChildText,
                  }));
                }
                logInfo(
                  "CHILD_VALUE_UNMATCHED",
                  "Child value did not match any option",
                  {
                    childFieldName,
                    prefillChildValue,
                    prefillChildText,
                    availableOptions: formattedOptions.length,
                  }
                );
              }
            }
          },

          onError: (err) => {
            // console.log("Error fetching additional fields:", err);
            setGlobalLoading(false);
            if (!isFromPrefill) {
              setApiError("Failed to load dependent fields. Please retry.");
            }
          },
        });
      } catch (error) {
        console.error("Exception in fetching additional fields:", error);
        setGlobalLoading(false);
        if (!isFromPrefill) {
          setApiError("Something went wrong. Please retry.");
        }
      }
    },
    [
      fieldForm.InputFields,
      formValues,
      selectedLanguageData?.code,
      org_code,
      category_code,
      botAdditionalFieldsRequest,
      logInfo,
    ]
  );

  // ✅ Extract value from response
  const extractValueFromResponse = useCallback(
    (field, dropdownOptions = []) => {
      if (!field.response) return null;

      const allOptions = [...(field.options || []), ...(dropdownOptions || [])];

      const matchedOption = allOptions.find(
        (opt) =>
          opt.Text === field.response ||
          opt.Text?.toLowerCase() === field.response?.toLowerCase() ||
          opt.Value === field.response ||
          opt.Value?.toLowerCase() === field.response?.toLowerCase()
      );

      return matchedOption ? matchedOption.Value : null;
    },
    []
  );

  // ✅ Prefill form values
  useEffect(() => {
    if (!isOpen || !fieldForm.InputFields?.length || isInitialPrefillComplete)
      return;

    const prefilled = {};
    const apiCallsToMake = [];
    const unmatchedInputs = {}; // Track unmatched user inputs

    // Make all fields visible initially
    setFieldForm((prev) => {
      const updatedForm = { ...prev };
      updatedForm.InputFields = updatedForm.InputFields.map((field) => ({
        ...field,
        IsHidden: false,
      }));
      return updatedForm;
    });

    // Method 1: Prefill from field_form_list
    if (Array.isArray(field_form_list) && field_form_list.length > 0) {
      field_form_list.forEach((fieldInfo) => {
        if (fieldInfo.UserInputValue || fieldInfo.UserInputText) {
          const userInputValue = fieldInfo.UserInputValue || "";
          const userInputText = fieldInfo.UserInputText || "";

          const field = fieldForm.InputFields?.find(
            (f) => f.FieldName === fieldInfo.FieldName
          );

          if (field && field.FieldType?.toLowerCase() === "dropdown") {
            // Get dropdown options for this field
            // const dropdownIndex = getDropdownIndexByFieldName(
            //   fieldInfo.FieldName
            // );
            const dropdownIndex = dropdownIndexMap[field.FieldName];
            const dropdownOptions = fieldForm.DropDown?.[dropdownIndex] || [];

            // Try to match by Value first
            let matchedOption = dropdownOptions.find(
              (opt) =>
                opt.Value?.toString().trim().toLowerCase() ===
                userInputValue?.toString().trim().toLowerCase()
            );

            // If no match by Value, try by Text
            if (!matchedOption && userInputText) {
              matchedOption = dropdownOptions.find(
                (opt) =>
                  opt.Text?.trim().toLowerCase() ===
                    userInputText.trim().toLowerCase() ||
                  opt.Text_regional?.trim().toLowerCase() ===
                    userInputText.trim().toLowerCase()
              );
            }

            if (matchedOption) {
              // Matched - set the value
              prefilled[fieldInfo.FieldName] = matchedOption.Value;
              logInfo("PREFILL_MATCHED", "User input matched dropdown option", {
                fieldName: fieldInfo.FieldName,
                userInputValue,
                userInputText,
                matchedValue: matchedOption.Value,
              });
            } else {
              // Not matched - store as unmatched input to show above field
              prefilled[fieldInfo.FieldName] = ""; // Keep field empty
              unmatchedInputs[fieldInfo.FieldName] =
                userInputText || userInputValue;
              logInfo(
                "PREFILL_UNMATCHED",
                "User input did not match any dropdown option",
                {
                  fieldName: fieldInfo.FieldName,
                  userInputValue,
                  userInputText,
                  availableOptions: dropdownOptions.length,
                }
              );
            }
          } else {
            // Non-dropdown field - just set the value
            prefilled[fieldInfo.FieldName] =
              userInputValue || userInputText || "";
          }

          // Handle child value
          if (fieldInfo.UserInputChildValue || fieldInfo.UserInputChildText) {
            const childFieldName = `${fieldInfo.FieldName}_child`;
            const childUserInputValue = fieldInfo.UserInputChildValue || "";
            const childUserInputText = fieldInfo.UserInputChildText || "";

            // Child options may not be loaded yet, store for later matching
            prefilled[childFieldName] =
              childUserInputValue || childUserInputText || "";

            // Store child unmatched text for display (will be cleared if matched after API call)
            if (childUserInputText) {
              unmatchedInputs[childFieldName] = childUserInputText;
            }
          }

          if (
            field &&
            (field.HasChilds || fieldInfo.UserInputChildValue) &&
            (prefilled[fieldInfo.FieldName] || userInputValue)
          ) {
            apiCallsToMake.push({
              field,
              value: prefilled[fieldInfo.FieldName] || userInputValue,
              childValue: fieldInfo.UserInputChildValue,
              childText: fieldInfo.UserInputChildText,
              order: field.FieldOrder,
            });
          }
        }
      });
    }

    // Method 2: Prefill from InputFields
    fieldForm.InputFields?.forEach((field) => {
      if (prefilled[field.FieldName] !== undefined) return; // Skip if already prefilled

      let valueToSet = null;
      let userProvidedText = null;

      if (field.UserInputValue !== null && field.UserInputValue !== undefined) {
        valueToSet = field.UserInputValue;
        userProvidedText = field.UserInputText;
      } else if (
        field.UserInputText !== null &&
        field.UserInputText !== undefined
      ) {
        valueToSet = field.UserInputText;
        userProvidedText = field.UserInputText;
      } else if (field.response) {
        valueToSet = field.response;
        userProvidedText = field.response;
      } else if (field.FieldValue) {
        valueToSet = field.FieldValue;
      }

      if (valueToSet !== null) {
        if (field.FieldType?.toLowerCase() === "dropdown") {
          // const dropdownIndex = getDropdownIndexByFieldName(field.FieldName);
          const dropdownIndex = dropdownIndexMap[field.FieldName];
          const dropdownOptions = fieldForm.DropDown?.[dropdownIndex] || [];

          // Try to match
          let matchedOption = dropdownOptions.find(
            (opt) =>
              opt.Value?.toString().trim().toLowerCase() ===
                valueToSet?.toString().trim().toLowerCase() ||
              opt.Text?.trim().toLowerCase() ===
                valueToSet?.toString().trim().toLowerCase()
          );

          if (matchedOption) {
            prefilled[field.FieldName] = matchedOption.Value;
          } else {
            prefilled[field.FieldName] = "";
            if (userProvidedText) {
              unmatchedInputs[field.FieldName] = userProvidedText;
            }
          }
        } else {
          prefilled[field.FieldName] = valueToSet;
        }

        if (field.UserInputChildValue) {
          prefilled[`${field.FieldName}_child`] = field.UserInputChildValue;
          if (field.UserInputChildText) {
            unmatchedInputs[`${field.FieldName}_child`] =
              field.UserInputChildText;
          }
        }

        if (field.FieldOrder === 1) {
          firstLevelValueRef.current = prefilled[field.FieldName] || valueToSet;
        }

        if (field.HasChilds || field.UserInputChildValue) {
          apiCallsToMake.push({
            field,
            value: prefilled[field.FieldName] || valueToSet,
            childValue: field.UserInputChildValue,
            childText: field.UserInputChildText,
            order: field.FieldOrder,
          });
        }
      }
    });

    // logInfo("PREFILL_VALUES", "Setting pre-filled values", {
    //   prefilled,
    //   unmatchedInputs,
    //   apiCallsToMake: apiCallsToMake.length,
    // });

    if (Object.keys(prefilled).length > 0) {
      setFormValues((prev) => ({ ...prev, ...prefilled }));
    }

    if (Object.keys(unmatchedInputs).length > 0) {
      setUnmatchedUserInputs(unmatchedInputs);
    }

    if (apiCallsToMake.length > 0) {
      const sortedCalls = [...apiCallsToMake].sort((a, b) => a.order - b.order);
      sortedCalls.forEach(({ field, value, childValue, childText }) => {
        callAdditionalFieldsAPI(field, value, true, childValue, childText);
      });
    }

    setIsInitialPrefillComplete(true);
  }, [
    isOpen,
    fieldForm,
    field_form_list,
    isInitialPrefillComplete,
    dropdownIndexMap,
    extractValueFromResponse,
    callAdditionalFieldsAPI,
    logInfo,
  ]);

  // ✅ Handle change
  const handleChange = useCallback(
    (field, value) => {
      logInfo("FIELD_VALUE_CHANGED", "Field value changed", {
        fieldName: field.FieldName,
        newValue: value,
      });

      const fieldName = field.FieldName;

      setFormValues((prev) => ({ ...prev, [fieldName]: value }));
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));

      // Clear unmatched input when user selects a valid option
      if (value && unmatchedUserInputs[fieldName]) {
        setUnmatchedUserInputs((prev) => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
      }

      if (isEditing) {
        setHasUnsavedChanges(true);
      }

      if (field.FieldOrder === 1) {
        firstLevelValueRef.current = value;
      }

      if (field.HasChilds) {
        const currentOrder = field.FieldOrder;

        // Find only dropdown children
        const childDropdownFields = fieldForm.InputFields.filter(
          (f) =>
            f.FieldOrder > currentOrder &&
            f.FieldType?.toLowerCase() === "dropdown"
        );

        // Clear only dropdown values
        setFormValues((prev) => {
          const updated = { ...prev };
          childDropdownFields.forEach(
            (child) => delete updated[child.FieldName]
          );
          return updated;
        });

        // Reset only dropdown option lists + hide them
        setFieldForm((prev) => {
          const updated = { ...prev };
          childDropdownFields.forEach((child) => {
            // const idx = getDropdownIndexByFieldName(child.FieldName);
            const idx = dropdownIndexMap[child.FieldName];
            // if (idx !== -1) updated.DropDown[idx] = [];
            if (idx !== undefined) {
              updated.DropDown[idx] = [];
            }

            updated.InputFields = updated.InputFields.map((f) =>
              f.FieldName === child.FieldName
                ? { ...f, options: [], IsHidden: true }
                : f
            );
          });
          return updated;
        });

        // Close dropdown UI
        setOpenDropdowns((prev) => {
          const updated = { ...prev };
          childDropdownFields.forEach(
            (child) => (updated[child.FieldName] = false)
          );
          return updated;
        });

        // Clear search terms for dropdowns only
        setSearchTerms((prev) => {
          const updated = { ...prev };
          childDropdownFields.forEach(
            (child) => (updated[child.FieldName] = "")
          );
          return updated;
        });

        // Fetch next-level dropdowns
        if (value) {
          callAdditionalFieldsAPI(field, value, false);
        }
      }
    },
    [isEditing, unmatchedUserInputs, callAdditionalFieldsAPI, logInfo]
  );

  // ✅ Field validation
  // const validateField = useCallback(
  //   (field) => {
  //     const value = formValues[field.FieldName] || "";
  //     if (field.IsMandatory && !value) {
  //       return t("requiredField") || "This field is required";
  //     }
  //     return "";
  //   },
  //   [formValues, t]
  // );

  // ✅ Field validation
  const validateField = useCallback(
    (field) => {
      const value = formValues[field.FieldName] || "";

      const panWordRegex = /\bpan\b/i;
      const pnrWordRegex = /\bpnr\b/i;
      const mobileWordRegex = /\b(mobile|phone|contact)\b/i;
      const uanWordRegex = /\buan\b/i;
      const ppoWordRegex = /\bppo\b/i; // ✅ NEW

      const isPanField =
        panWordRegex.test(field.FieldName) ||
        panWordRegex.test(field.DisplayLable);

      const isPnrField =
        pnrWordRegex.test(field.FieldName) ||
        pnrWordRegex.test(field.DisplayLable);

      const isMobileField =
        mobileWordRegex.test(field.FieldName) ||
        mobileWordRegex.test(field.DisplayLable);

      const isUanField =
        uanWordRegex.test(field.FieldName) ||
        uanWordRegex.test(field.DisplayLable);

      const isPpoField =
        ppoWordRegex.test(field.FieldName) ||
        ppoWordRegex.test(field.DisplayLable); // ✅ NEW

      // PAN validation
      if (isPanField) {
        if (!value) {
          return t("validateField") || "This field is required";
        }
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(value.trim().toUpperCase())) {
          return t("Invalid PAN format (e.g. ABCDE1234F)");
        }
      }

      // PNR validation
      if (isPnrField) {
        if (!value) {
          return t("validateField") || "This field is required";
        }
        const pnrRegex = /^[0-9]{10}$/;
        if (!pnrRegex.test(value.trim())) {
          return t("Invalid PNR number (e.g. 1234567890)");
        }
      }

      // Mobile validation
      if (isMobileField) {
        if (!value) {
          return t("validateField") || "This field is required";
        }
        const mobileRegex = /^[6-9][0-9]{9}$/;
        if (!mobileRegex.test(value.trim())) {
          return t(
            "Invalid mobile number (e.g. mobile number must be 10 digit)"
          );
        }
      }

      // UAN validation
      if (isUanField) {
        if (!value) {
          return t("validateField") || "This field is required";
        }
        const uanRegex = /^[0-9]{12}$/;
        if (!uanRegex.test(value.trim())) {
          return t("Invalid UAN number (e.g. 123456789012)");
        }
      }

      // ✅ PPO validation (NEW)
      if (isPpoField) {
        if (!value) {
          return t("validateField") || "This field is required";
        }
        const ppoRegex = /^[0-9]{12}$/;
        if (!ppoRegex.test(value.trim())) {
          return t("Invalid PPO number (e.g. 123456789012)");
        }
      }

      return "";
    },
    [formValues, t]
  );

  // ✅ Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    const newShakingFields = {};
    let isValid = true;

    fieldForm.InputFields.forEach((field) => {
      if (!field.IsHidden) {
        const error = validateField(field);

        if (error) {
          newErrors[field.FieldName] = error;
          newShakingFields[field.FieldName] = true;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    setShakingFields(newShakingFields);

    if (!isValid) {
      setTimeout(() => {
        setShakingFields({});
      }, 500);
    }

    return {
      isValid,
      errors: newErrors,
    };
  }, [fieldForm.InputFields, validateField]);

  // ✅ Check if field is enabled
  const isFieldEnabled = useCallback(
    (field) => {
      if (!isEditing) return false;

      if (field.FieldOrder > 1) {
        const parentField = fieldForm.InputFields.find(
          (f) => f.FieldOrder === field.FieldOrder - 1 && f.HasChilds
        );

        if (parentField && !formValues[parentField.FieldName]) {
          return false;
        }
      }

      return true;
    },
    [isEditing, fieldForm.InputFields, formValues]
  );

  const handleSearchOptionsUpdate = useCallback(
    (fieldName, options = []) => {
      if (!fieldName) return;

      setFieldForm((prev) => {
        const updated = { ...prev };

        const idx = dropdownIndexMap?.[fieldName];
        if (idx === undefined) return prev;

        // Update DropDown list
        updated.DropDown = Array.isArray(prev.DropDown)
          ? [...prev.DropDown]
          : [];
        updated.DropDown[idx] = options;

        // Sync InputFields options
        updated.InputFields = prev.InputFields.map((f) =>
          f.FieldName === fieldName ? { ...f, options, IsHidden: false } : f
        );

        return updated;
      });
    },
    [dropdownIndexMap]
  );

  const getFuzzyMatchForField = useCallback(
    (fieldName, unmatchedText, dropdownOptions) => {
      if (!unmatchedText || !dropdownOptions.length) return null;

      const isRegional = selectedLanguageData?.code?.toLowerCase() !== "en";

      // Prepare searchable list
      const searchableList = dropdownOptions.map((opt) => ({
        value: opt.Value,
        text: opt.Text || "",
        textRegional: opt.Text_regional || "",
        displayText: isRegional ? opt.Text_regional || opt.Text : opt.Text,
        original: opt,
      }));

      // Create Fuse instance for this field
      const fuse = new Fuse(searchableList, {
        includeScore: true,
        threshold: 0.4, // 60% match (Fuse score 0.3 = ~70% match)
        keys: [
          { name: "displayText", weight: 1.0 },
          { name: "text", weight: 0.7 },
          { name: "textRegional", weight: 0.7 },
        ],
      });

      const results = fuse.search(unmatchedText);

      if (results.length > 0) {
        // Filter for 70-80% matches (Fuse score 0.2-0.3)
        const goodMatches = results.filter((r) => r.score <= 0.4).slice(0, 3);

        if (goodMatches.length > 0) {
          // console.log("FUZZY_MATCH_FOUND", "Found 70-80% fuzzy matches", {
          //   fieldName,
          //   unmatchedText,
          //   matchCount: goodMatches.length,
          //   bestScore: goodMatches[0].score,
          // });

          return goodMatches.map((match) => ({
            value: match.item.value,
            text: match.item.displayText,
            score: match.score,
            matchPercentage: Math.round((1 - match.score) * 100),
          }));
        }
      }

      return null;
    },
    [selectedLanguageData?.code]
  );

  // ✅ Render dropdown (like BotDynamicForm)
  const renderDropdown = useCallback(
    (field) => {
      const dropdownOptions = getDropdownOptions(field);
      const isEnabled = isFieldEnabled(field);
      const currentValue = formValues[field.FieldName] || "";
      const isRegional = selectedLanguageData?.code?.toLowerCase() !== "en";
      const isOpen = openDropdowns[field.FieldName] || false;
      const searchTerm = searchTerms[field.FieldName] || "";
      const hasUnmatchedInput = unmatchedUserInputs[field.FieldName];

      const selectedOption = dropdownOptions.find(
        (opt) => opt.Value?.toString() === currentValue?.toString()
      );

      let displayValue = "";

      if (selectedOption) {
        if (isRegional) {
          displayValue = selectedOption.Text_regional || selectedOption.Text;
        } else {
          displayValue = selectedOption.Text;
        }
      }

      const filteredOptions = dropdownOptions.filter((option) => {
        const displayText = isRegional
          ? option.Text_regional || option.Text || ""
          : option.Text || "";
        return displayText.toLowerCase().includes(searchTerm.toLowerCase());
      });

      const fuzzyMatches = hasUnmatchedInput
        ? getFuzzyMatchForField(
            field.FieldName,
            hasUnmatchedInput,
            filteredOptions
          )
        : null;

      const handleAcceptFuzzyMatch = (matchValue) => {
        handleChange(field, matchValue);
        setUnmatchedUserInputs((prev) => {
          const updated = { ...prev };
          delete updated[field.FieldName];
          return updated;
        });
        logInfo("FUZZY_MATCH_ACCEPTED", "User accepted fuzzy match", {
          fieldName: field.FieldName,
          originalInput: hasUnmatchedInput,
          matchedValue: matchValue,
        });
      };

      const handleSelect = (value) => {
        handleChange(field, value);
        setOpenDropdowns((prev) => ({ ...prev, [field.FieldName]: false }));
        setSearchTerms((prev) => ({ ...prev, [field.FieldName]: "" }));
      };

      // ✅ Handle both onChange and onChangeText for KpmgTransliterate
      const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerms((prev) => ({
          ...prev,
          [field.FieldName]: value.trim(),
        }));
      };

      // ✅ Handle transliterated text change
      const handleSearchChangeText = (value) => {
        setSearchTerms((prev) => ({
          ...prev,
          [field.FieldName]: value.trim(),
        }));
      };

      const getPortalStyle = () => {
        if (!dropdownRefs.current[field.FieldName]) return {};

        const rect =
          dropdownRefs.current[field.FieldName].getBoundingClientRect();
        const approxHeight = 240;

        const spaceAbove = rect.top;
        const spaceBelow = window.innerHeight - rect.bottom;

        const top =
          spaceAbove > approxHeight && spaceBelow < approxHeight
            ? rect.top - approxHeight - 8
            : rect.bottom + 8;

        return {
          left: rect.left,
          top,
          width: rect.width,
        };
      };

      return (
        <div key={field.FieldName} className="w-full">
          {/* Show unmatched user input warning */}
          {/* {hasUnmatchedInput && (
                    <div className="mb-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                        <p className="text-xs text-gray-700 dark:text-gray-300">
                            <span className="font-medium">{'User entered data'}: </span>
                            <span className="text-amber-700 dark:text-amber-400 font-semibold">"{hasUnmatchedInput}"</span>
                        </p>
                    </div>
                )} */}
          {hasUnmatchedInput && (
            <div className="mb-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="w-5 h-5 text-amber-600 dark:text-amber-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mb-1">
                    {t("verifyDataModel.userProvidedInput")}:{" "}
                    <span className="text-amber-700 dark:text-amber-400 font-semibold">
                      "{hasUnmatchedInput}"
                    </span>
                  </p>

                  {/* {fuzzyMatches && fuzzyMatches.length > 0 ? (
                                    <div className="mt-2">
                                        <div className="space-y-1">
                                            {fuzzyMatches.map((match, idx) => (
                                                <button
                                                    key={match.value}
                                                    onClick={() => handleAcceptFuzzyMatch(match.value)}
                                                    className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${idx === 0
                                                        ? 'bg-blue-100 dark:bg-blue-900/40 border border-blue-300 dark:border-blue-700 hover:bg-blue-200 dark:hover:bg-blue-900/60'
                                                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                        }`}
                                                >
                                                    <span className="font-medium text-gray-900 dark:text-white">
                                                        {match.text}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        No close matches found. Please select from the dropdown below.
                                    </p>
                                )} */}
                </div>
              </div>
            </div>
          )}

          <div
            tabIndex={-1}
            ref={(el) => {
              if (dropdownRefs.current) {
                dropdownRefs.current[field.FieldName] = el;
              }
              if (errorRefs.current) {
                errorRefs.current[field.FieldName] = el;
              }
            }}
            className={`border rounded-md px-3 py-2
               ${shakingFields[field.FieldName] ? "animate-shake" : ""}
              ${
                isEnabled
                  ? "cursor-pointer bg-white dark:bg-gray-800"
                  : "bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
              } ${
              hasUnmatchedInput && !currentValue
                ? "border-amber-400"
                : "border-gray-300"
            } flex justify-between items-center`}
            onClick={() =>
              isEnabled &&
              setOpenDropdowns((prev) => ({
                ...prev,
                [field.FieldName]: !isOpen,
              }))
            }
          >
            <span
              className={
                !currentValue
                  ? "text-gray-400"
                  : "text-gray-900 dark:text-white"
              }
            >
              {displayValue || "Select..."}
            </span>

            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {hasUnmatchedInput && (
            <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
              <div className="flex-1">
                {/* <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mb-1">
                                    User entered: <span className="text-amber-700 dark:text-amber-400 font-semibold">"{hasUnmatchedInput}"</span>
                                </p> */}

                {fuzzyMatches && fuzzyMatches.length > 0 ? (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {fuzzyMatches.map((match, idx) => (
                        <button
                          key={match.value}
                          onClick={() => handleAcceptFuzzyMatch(match.value)}
                          className={`
            inline-flex items-center gap-1
            px-3 py-1.5
            rounded-full text-xs font-medium
            border transition-all
            ${
              idx === 0
                ? "bg-blue-100 text-blue-900 border-blue-300 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-700 dark:hover:bg-blue-900/60"
                : "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"
            }
          `}
                        >
                          {match.text}
                          {/* optional confidence */}
                          {/* <span className="opacity-70">({match.matchPercentage}%)</span> */}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    No close matches found. Please select from the dropdown
                    below.
                  </p>
                )}
              </div>
            </div>
          )}

          {isOpen &&
            isEnabled &&
            createPortal(
              <div
                ref={(el) => (portalRefs.current[field.FieldName] = el)}
                className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-2xl max-h-60 overflow-visible"
                style={getPortalStyle()}
              >
                <div className="sticky top-0 bg-white dark:bg-gray-800 p-2 border-b">
                  {/* <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerms(prev => ({ ...prev, [field.FieldName]: e.target.value }))}
                                className="w-full px-2 py-1 border rounded text-sm dark:bg-gray-700 dark:text-white"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            /> */}
                  <KpmgTransliterate
                    type="text"
                    lang={selectedLanguageData?.code || "en"}
                    placeholder={
                      selectedLanguageData?.code === "en"
                        ? field.DisplayLable
                        : field.DisplayLable_regional || "Search..."
                    }
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onChangeText={handleSearchChangeText}
                    className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    enabled={isRegional}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div className="max-h-48 overflow-y-auto">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt, i) => (
                      <div
                        key={opt.Value}
                        className={`px-3 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700 ${
                          opt.Value === currentValue
                            ? "bg-blue-100 dark:bg-blue-900"
                            : ""
                        }`}
                        onClick={() => !opt.Disabled && handleSelect(opt.Value)}
                      >
                        <span className="text-gray-900 dark:text-white">
                          {isRegional
                            ? opt.Text_regional || opt.Text
                            : opt.Text}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-center text-gray-500">
                      No options found
                    </div>
                  )}
                </div>
              </div>,
              document.body
            )}
        </div>
      );
    },
    [
      getDropdownOptions,
      isFieldEnabled,
      formValues,
      selectedLanguageData?.code,
      openDropdowns,
      searchTerms,
      unmatchedUserInputs,
      handleChange,
      fieldForm.DropDown,
    ]
  );

  const handleTextChange = useCallback(() => {}, []);

  // ✅ Render field
  const renderField = useCallback(
    (field) => {
      const value = formValues[field.FieldName] || "";
      const hasError = errors[field.FieldName];
      const isDisabled = !isEditing || !isFieldEnabled(field);
      const fieldType = field.FieldType?.toLowerCase() || "text";

      const getInputClasses = (extraClasses = "") => {
        return `w-full px-3 py-2 rounded border transition-all duration-200 ${
          hasError
            ? "border-red-500 focus:ring-red-500 focus:border-red-500"
            : "border-gray-300 focus:ring-[#FFD6A8] focus:border-[#FFD6A8]"
        } ${
          isEditing && !isDisabled
            ? "text-gray-900 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2"
            : "bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed"
        } ${extraClasses}`;
      };

      if (fieldType === "dropdown") {
        if (field.IsSearchable) {
          const hasUnmatchedInput = unmatchedUserInputs[field.FieldName];
          return (
            <div>
              {hasUnmatchedInput && (
                <div className="mb-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{"User entered data"}: </span>
                    <span className="text-amber-700 dark:text-amber-400 font-semibold">
                      "{hasUnmatchedInput}"
                    </span>
                  </p>
                </div>
              )}
              <ApiSearchDropdown
                hasUnmatchedInput={hasUnmatchedInput}
                field={field}
                onChange={handleChange}
                onOptionsUpdate={handleSearchOptionsUpdate}
              />
            </div>
          );
        } else {
          return renderDropdown(field);
        }
      }

      if (fieldType === "textbox" || fieldType === "text") {
        if (selectedLanguageData?.code !== "en" && isEditing) {
          return (
            <KpmgTransliterate
              type="text"
              lang={selectedLanguageData?.code || "en"}
              className={getInputClasses(shakingFields[field.FieldName] ? "animate-shake" : "")}
              value={value}
              placeholder={
                selectedLanguageData?.code === "en"
                  ? field.DisplayLable
                  : field.DisplayLable_regional
              }
              onChange={(e) => handleChange(field, e.target.value)}
              disabled={!isEditing}
              enabled={selectedLanguageData?.code !== "en"}
              onChangeText={(e) => handleTextChange(field.FieldName, e.value)}
            />
          );
        }

        return (
          <input
            ref={(el) => {
              if (errorRefs.current) {
                errorRefs.current[field.FieldName] = el;
              }
            }}
            type="text"
            value={value}
            onChange={(e) => handleChange(field, e.target.value)}
            disabled={isDisabled}
            placeholder={
              selectedLanguageData?.code === "en"
                ? field.DisplayLable
                : field.DisplayLable_regional
            }
            className={getInputClasses(shakingFields[field.FieldName] ? "animate-shake" : "")}
          />
        );
      }

      if (fieldType === "textarea") {
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(field, e.target.value)}
            disabled={isDisabled}
            placeholder={
              selectedLanguageData?.code === "en"
                ? field.DisplayLable
                : field.DisplayLable_regional
            }
            className={getInputClasses(shakingFields[field.FieldName] ? "animate-shake" : "")}
            rows={4}
          />
        );
      }

      return (
        <input
          ref={(el) => {
            if (errorRefs.current) {
              errorRefs.current[field.FieldName] = el;
            }
          }}
          type="text"
          value={value}
          onChange={(e) => handleChange(field, e.target.value)}
          disabled={isDisabled}
          placeholder={
            selectedLanguageData?.code === "en"
              ? field.DisplayLable
              : field.DisplayLable_regional
          }
          className={getInputClasses(shakingFields[field.FieldName] ? "animate-shake" : "")}
        />
      );
    },
    [
      formValues,
      errors,
      isEditing,
      isFieldEnabled,
      selectedLanguageData?.code,
      renderDropdown,
      handleChange,
      handleTextChange,
    ]
  );

  // ✅ Auto-save functionality
  const autoSave = useCallback(async () => {
    if (!hasUnsavedChanges || !isEditing) return;

    setIsAutoSaving(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      setHasUnsavedChanges(false);
    } catch (error) {
      logInfo("AUTO_SAVE_ERROR", "Auto-save failed", { error });
    } finally {
      setIsAutoSaving(false);
    }
  }, [hasUnsavedChanges, isEditing, logInfo]);

  // ✅ Auto-save effect
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (hasUnsavedChanges && isEditing) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave();
      }, 2000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [hasUnsavedChanges, isEditing, autoSave]);

  const userInfoFocus = () => {
    if (userInfoSectionRef.current) {
      userInfoSectionRef.current.focus();
      userInfoSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // ✅ Focus on open
  useEffect(() => {
    if (isOpen && userInfoSectionRef.current) {
      const timer = setTimeout(() => {
        userInfoFocus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      userInfoSectionRef.current &&
      !globalLoading &&
      isInitialPrefillComplete
    ) {
      const timer = setTimeout(() => {
        if (userInfoSectionRef.current) {
          // console.log("SCROLLING_TO_USER_INFO_SECTION");

          // First ensure the element is visible and in DOM
          userInfoSectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });

          // Then focus with a small delay
          setTimeout(() => {
            if (userInfoSectionRef.current) {
              userInfoSectionRef.current.focus();

              // Add a visual indicator

              userInfoSectionRef.current.style.transition =
                "box-shadow 0.3s ease";

              setTimeout(() => {
                if (userInfoSectionRef.current) {
                  userInfoSectionRef.current.style.boxShadow = "";
                }
              }, 2000);
            }
          }, 100);
        }
      }, 100); // Reduced delay since prefill is already complete

      return () => clearTimeout(timer);
    }
  }, [isOpen, globalLoading, isInitialPrefillComplete]);

  // ✅ Edit/Save toggle handler
  // const handleEditSaveToggle = async () => {
  //     if (isEditing) {
  //         if (!validateForm()) {
  //             logInfo('SAVE_VALIDATION_FAILED', 'Form validation failed on save');
  //             return;
  //         }

  //         setIsSaving(true);
  //         try {
  //             await new Promise(resolve => setTimeout(resolve, 800));
  //             setHasUnsavedChanges(false);
  //             setIsEditing(false);
  //             logInfo('MANUAL_SAVE_COMPLETED', 'Form manually saved successfully', formValues);
  //         } catch (error) {
  //             logInfo('MANUAL_SAVE_ERROR', 'Manual save failed', { error });
  //         } finally {
  //             setIsSaving(false);
  //         }
  //     } else {
  //         setIsEditing(true);
  //     }
  // };

  const autoFocusErrorOnSubmit = (validationErrors) => {
    const firstErrorField = fieldForm.InputFields.find(
      (field) => validationErrors[field.FieldName]
    );

    if (firstErrorField) {
      const element = errorRefs.current[firstErrorField.FieldName];

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        setTimeout(() => {
          if (typeof element.focus === "function") {
            element.focus();
          }
        }, 300);
      }
    }
  };

  // ✅ Submit handler
  const handleSubmit = async () => {
    logInfo("SUBMIT_STARTED", "Starting form submission", { formValues });

    const validationResult = validateForm();

    if (!validationResult.isValid) {
      logInfo("SUBMIT_VALIDATION_FAILED", "Form validation failed on submit");

      autoFocusErrorOnSubmit(validationResult.errors);

      return;
    }

    dispatch(setIsGrievanceRegistering(true));
    setIsSubmitting(true);
    try {
      const dropdownFields = fieldForm.InputFields.filter(
        (field) =>
          field.FieldType?.toLowerCase() === "dropdown" && !field.IsHidden
      ).sort((a, b) => a.FieldOrder - b.FieldOrder);

     let finalDropdownListValue = "";

// For Service Provider related complaints
if (formValues?.ServiceProvider) {
  finalDropdownListValue =
    formValues.ServiceProvider;
}
// For other department/category dropdowns
else {
  const lastDropdownField =
    dropdownFields[
      dropdownFields.length - 1
    ];

  finalDropdownListValue =
    lastDropdownField
      ? formValues[
          lastDropdownField.FieldName
        ] || ""
      : "";
}

console.log(
  "FinalDropdownListValue:",
  finalDropdownListValue
);
      // fileData
      // {
      //     name: string,
      //     type: string,
      //     size: number,
      //     buffer: ArrayBuffer,
      //     base64: string
      //   }
      // console.log(`fileData`, { fileData });

      //const sanitizeRemarks = (text = "") =>
      //  text
        //  .replace(/[^a-zA-Z0-9 ]/g, "")
          //.replace(/\s+/g, " ")
          //.trim();

      let additionalData = {
        OrgCode: org_code,
        FinalCategory: category_code,
        FinalDropdownListValue: finalDropdownListValue,
        Remarks: `${
          summary || serverRemarks || updated_grievance
        } \n\n\n\n ${notes}`,
        IpAddress: await getClientIP(),
        Document: fileData?.base64 ?? "",
        Language: selectedLanguageData?.code || "en",
        UserCode: userData?.id,
      };

      // console.log(`additionalData before submit`, {
      //   additionalData,
      // });

      await handleSubmitGrievance(formValues, additionalData);

      logInfo("SUBMIT_COMPLETED", "Form submitted successfully", {
        formValues,
      });
      
      closeModal();
    } catch (error) {
      // console.log(`Error is`, error);
      logInfo("SUBMIT_ERROR", "Form submission failed", { error });
    } finally {
      setIsSubmitting(false);
      // dispatch(setIsGrievanceRegistering(false));
    }
  };

  const toggleGrievanceExpansion = () => {
    setIsGrievanceExpanded((prev) => !prev);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      // "image/png",
      // "image/jpeg",
      "application/pdf",
      // "application/msword",
      // "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type");
      return;
    }

    const arrayBuffer = await file.arrayBuffer();

    // Convert to Base64 (useful for API payloads)
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      )
    );

    dispatch(
      setGrievanceFileData({
        name: file.name,
        type: file.type,
        size: file.size,
        // buffer: arrayBuffer,
        base64,
      })
    );
  };
  // const handleFileUpload = async (e) => {
  //     const file = e.target.files[0];
  //     if (!file) return;

  //     const allowedTypes = [
  //         // "image/png",
  //         // "image/jpeg",
  //         "application/pdf",
  //         // "application/msword",
  //         // "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  //     ];

  //     if (!allowedTypes.includes(file.type)) {
  //         alert("Invalid file type");
  //         return;
  //     }

  //     const arrayBuffer = await file.arrayBuffer();

  //     // Convert to Base64 - using chunked approach to avoid call stack issues
  //     const uint8Array = new Uint8Array(arrayBuffer);
  //     let binary = '';
  //     const chunkSize = 0x8000; // 32KB chunks

  //     for (let i = 0; i < uint8Array.length; i += chunkSize) {
  //         const chunk = uint8Array.subarray(i, i + chunkSize);
  //         binary += String.fromCharCode.apply(null, chunk);
  //     }

  //     const base64 = btoa(binary);

  //     dispatch(setGrievanceFileData({
  //         name: file.name,
  //         type: file.type,
  //         size: file.size,
  //         // buffer: arrayBuffer,
  //         base64
  //     }));
  // };

  // const handleFileUpload = async (e) => {
  //     const file = e.target.files[0];
  //     if (!file) return;

  //     const allowedTypes = ["application/pdf"];

  //     if (!allowedTypes.includes(file.type)) {
  //         alert("Invalid file type");
  //         return;
  //     }

  //     // Convert to Base64 using FileReader
  //     const base64 = await new Promise((resolve, reject) => {
  //         const reader = new FileReader();
  //         reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data:...;base64, prefix
  //         reader.onerror = reject;
  //         reader.readAsDataURL(file);
  //     });

  //     dispatch(setGrievanceFileData({
  //         name: file.name,
  //         type: file.type,
  //         size: file.size,
  //         base64
  //     }));
  // };

  const handleViewFile = () => {
    // if (!fileData) return;

    // const blob = new Blob([fileData.buffer], { type: fileData.type });
    // const url = URL.createObjectURL(blob);
    // window.open(url, "_blank");
    if (!fileData) return;
    setIsViewerOpen(true);
  };

  const handleDeleteFile = () => {
    dispatch(setGrievanceFileData(null));
    // IMPORTANT: reset file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // ✅ Early return if modal not open
  if (!isOpen) return null;

  const allFields = [...fieldForm.InputFields].sort(
    (a, b) => a.FieldOrder - b.FieldOrder
  );

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm bg-black bg-opacity-40">
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-[90vw] sm:w-[75vw] md:w-1/2 relative flex flex-col max-h-[80%]">
          <h2 className="sticky top-0 text-xl font-semibold mb-4 px-5 py-3 border-4 border-[#FFD6A8] bg-gradient-to-b from-[#FFF7ED] via-[#FFFBEB] to-[#FFF7ED] text-black rounded-t-lg">
            {t("verifyDetails")}
          </h2>

          {/* SUBMIT LOADER OVERLAY */}
          {isGrievanceRegistering && (
            <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-white/80 dark:bg-black/70">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl px-6 py-5 flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-[#FFD6A8] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t("registerningGrievance")}
                </p>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-auto relative">
            {/* Collapsible Grievance Summary */}
            <div className="px-5 py-2">
              <div className="bg-[#FFF7ED]  rounded-lg border border-[#FFD6A8] dark:border-indigo-600 shadow-sm">
                {/* <button
                  onClick={toggleGrievanceExpansion}
                  className="w-full flex items-center justify-between p-4 hover:bg-indigo-100 dark:hover:bg-indigo-700 transition-colors duration-200 rounded-lg"
                > */}
                <p className="text-md text-gray-700 dark:text-gray-300 font-medium px-5 py-3">
                  {t("grievanceSummery")}
                </p>
                {/* {isGrievanceExpanded ? (
                    <ChevronUp
                      size={20}
                      className="text-gray-600 dark:text-gray-400"
                    />
                  ) : (
                    <ChevronDown
                      size={20}
                      className="text-gray-600 dark:text-gray-400"
                    />
                  )}
                </button> */}
                {/* {isGrievanceExpanded && ( */}
                {
                  <div className="px-4 pb-4 transition-all duration-300 ease-in-out">
                    <div className="relative mt-1">
                      <KpmgTransliterate
                        lang={selectedLanguageData?.code || "en"}
                        value={summary}
                        onChangeText={handleSummaryChange}
                        enabled={selectedLanguageData?.code !== "en"}
                        containerClassName="w-full"
                        renderComponent={(props) => (
                          <TextareaAutosize
                            {...props}
                            minRows={4}
                            maxRows={10}
                            placeholder={t("typeHere") || "Edit summary…"}
                            className="
                                        w-full resize-none text-sm px-3 py-3 pr-20  rounded-md border border-[#FFD6A8]
                                          focus:outline-none focus:ring-[#FFD6A8] focus:border-[#FFD6A8]
                                        dark:bg-[#1f1f1f] dark:text-white
                                      "
                          />
                        )}
                      />

                      {/* 🔄 Status Indicator (top-right inside textarea area) */}
                      <div className="absolute top-2 right-3 flex items-center gap-1 text-[11px]">
                        {isSummarySaving && (
                          <div className="flex items-center gap-1 text-blue-600 animate-pulse">
                            <span className="w-3 h-3 border-2 border-[#FFD6A8] border-t-transparent rounded-full animate-spin" />
                            <span>{t("saving") || "Saving…"}</span>
                          </div>
                        )}

                        {!isSummarySaving && isSummarySaved && (
                          <div className="flex items-center gap-1 text-green-600 animate-fade-in">
                            <span>✔</span>
                            <span>{t("saved") || "Saved"}</span>
                          </div>
                        )}

                        {!isSummarySaving && !isSummarySaved && (
                          <div className="text-gray-400 italic">
                            {t("editing") || "Editing"}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>

            {mandatory_data_present &&
              grievance_bucket &&
              Object.keys(grievance_bucket).length > 0 && (
                <div className="bg-white dark:bg-[#232323] border border-[#FFD6A8] dark:border-[#FFD6A8] rounded-lg shadow-sm mx-5 my-2">
                  <h4 className="text-lg font-semibold text-black dark:text-blue-400 px-5 py-3 border-b border-[#FFD6A8] dark:border-[#FFD6A8]">
                    {t("categorization.title")}
                  </h4>
                  <div className="space-y-3 text-sm px-5 py-3">
                    {Object.entries(grievance_bucket)
                      // .filter(([k, v]) => k !== 'Description' && v)
                      .filter(([k, v]) => {
                        if (k === "Description" || !v) return false;
                        if (isNotSubcategory && k === "SubCategory")
                          return false;
                        return true;
                      })
                      .map(([k, v], i) => (
                        <div key={k} className="flex flex-col gap-1">
                          <p className="font-medium text-gray-800 dark:text-gray-200">
                            {/* {t(`categorization.${k}`) || k} */}
                            {k === "Category" && isOrganisationCategory
                              ? t("categorization.Organisation") ||
                                "Organisation"
                              : t(`categorization.${k}`) || k}
                          </p>
                          <p className="text-gray-700 dark:text-gray-300 bg-[#F9FAFB]  border border-[#FFD6A8] px-3 py-2 rounded-md">
                            {String(v)}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {allFields.length > 0 && (
              <div
                ref={userInfoSectionRef}
                tabIndex={-1}
                className="relative bg-white dark:bg-[#232323] border border-[#FFD6A8] dark:border-[#FFD6A8] rounded-lg shadow-sm mx-5 my-2 focus:outline-none  outline-none overflow-hidden"
              >
                {/* Loading Overlay */}
                {globalLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-black/60 z-20">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mb-3"></div>
                      <p className="text-gray-700 dark:text-gray-200 text-base font-medium">
                        {t("loadingData") || "Loading, please wait..."}
                      </p>
                    </div>
                  </div>
                )}

                {/* API Error Overlay */}
                {apiError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-black/60 z-20">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center">
                      <p className="text-red-600 text-base mb-3 font-medium">
                        {apiError}
                      </p>
                      <button
                        type="button"
                        onClick={() => setApiError(null)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-[#FFD6A8] transition"
                      >
                        {t("retry") || "Retry"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Header */}
                <div className="flex justify-between items-center px-5 py-3 border-b border-[#FFD6A8] dark:border-[#FFD6A8]">
                  <h4 className="text-lg font-semibold text-black flex items-center gap-2">
                    {t("collectedUserInformation")}
                  </h4>
                  {/* <EditSaveButton
                                        isSaving={isSaving}
                                        isAutoSaving={isAutoSaving}
                                        hasUnsavedChanges={hasUnsavedChanges}
                                        isEditing={isEditing}
                                        onToggle={handleEditSaveToggle}
                                        t={t}
                                    /> */}
                </div>

                {/* Form Fields */}
                <div
                  className="space-y-4 text-sm px-5 py-4 relative z-10 
  [&_input]:border [&_input]:border-[#FFD6A8] 
  [&_input:focus]:ring-2 [&_input:focus]:ring-[#FFD6A8] 
  [&_input]:outline-none
"
                >
                  {allFields.map((field) => {
                    const panWordRegex = /\bpan\b/i;
                    const isPanField =
                      panWordRegex.test(field.FieldName) ||
                      panWordRegex.test(field.DisplayLable);

                    return (
                      <div
                        key={field.FieldName}
                        className="flex flex-col gap-2"
                      >
                        <label className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {selectedLanguageData?.code === "en"
                            ? field.DisplayLable
                            : field.DisplayLable_regional}
                          {isPanField && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </label>

                        {renderField(field)}

                        {errors[field.FieldName] && (
                          <p ref={errorRefs} className="text-sm text-red-600">
                            {errors[field.FieldName]}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ROW 1 — Upload Section */}
          <div className="bg-white dark:bg-[#232323]  border border-[#FFD6A8] rounded-lg shadow-sm p-3  mr-7 ml-5">
            <div className="flex items-center gap-4">
              {/* Upload */}
              <label className="flex items-center gap-2 cursor-pointer text-[#4A5565] hover:text-[#FF8900]">
                <Upload className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {t("supportDocument") || "Upload document"}
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>

              {/* View */}
              <button
                type="button"
                onClick={handleViewFile}
                disabled={!fileData}
                className={`${
                  fileData
                    ? "text-gray-700 hover:text-gray-900"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                <Eye className="w-5 h-5" />
              </button>

              {/* Delete */}
              <button
                type="button"
                onClick={handleDeleteFile}
                disabled={!fileData}
                className={`${
                  fileData
                    ? "text-red-600 hover:text-red-700"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                <Trash2 className="w-5 h-5" />
              </button>

              {fileData && (
                <span className="text-xs text-green-600 truncate max-w-[200px]">
                  {fileData.name}
                </span>
              )}
            </div>
          </div>

          {/* ✨ Collapsible edit area */}
          <div className=" bg-white dark:bg-[#232323]  border border-[#FFD6A8] border rounded-lg shadow-sm mt-1  mr-7 ml-5">
            {/* Header */}
            <button
              type="button"
              onClick={() => setIsEditOpen((v) => !v)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-black dark:text-black"
            >
              <span>{t("additionalNotes") || "Additional Notes"}</span>
              <span className="text-xs text-gray-500">
                {isEditOpen ? t("collapse") : t("expand")}
              </span>
            </button>

            {/* Editor */}
            {isEditOpen && (
              <div className="p-3 border-t border-[#FFD6A8] dark:border-[#FFD6A8]">
                <div className="relative">
                  <KpmgTransliterate
                    lang={selectedLanguageData?.code || "en"}
                    value={notes}
                    onChangeText={handleNotesChange}
                    containerClassName="w-full"
                    enabled={selectedLanguageData?.code !== "en"}
                    renderComponent={(props) => (
                      <TextareaAutosize
                        {...props}
                        minRows={3}
                        maxRows={6}
                        placeholder={t("typeHere") || "Type here…"}
                        className="
          w-full resize-none text-sm px-3 py-2 pr-10 border rounded-md border-[#FFD6A8]
          focus:outline-none focus:ring-[#FFD6A8] focus:border-[#FFD6A8]
          dark:bg-[#1f1f1f] dark:text-white
        "
                      />
                    )}
                  />

                  {/* 🎤 MIC BUTTON */}
                  <div className="absolute bottom-2 right-2 group">
                    {/* Tooltip */}
                    <div
                      className="
    absolute bottom-full right-0 mb-2
    px-2 py-1 text-[11px] text-white rounded-md whitespace-nowrap
    opacity-0 group-hover:opacity-100
    pointer-events-none transition-opacity duration-200
    bg-[#FF6900]
  "
                    >
                      {isRecording ? t("tapToStop") : t("tapToSpeak")}
                      {/* Arrow — must match bg color */}
                      <div
                        className="absolute top-full right-3 w-0 h-0
      border-l-4 border-r-4 border-t-4
      border-l-transparent border-r-transparent
      border-t-[#FF6900]"
                      />
                    </div>

                    {/* Mic button */}
                    <div
                      onClick={() => setIsRecording((r) => !r)}
                      className={`
      relative rounded-full p-1.5 transition-all duration-300 cursor-pointer
      flex items-center justify-center
      ${
        isRecording
          ? "bg-red-500 dark:bg-red-600"
          : "bg-[#FF6900] dark:bg-[#FF6900]"
      }
    `}
                    >
                      {isRecording && (
                        <>
                          <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60 animate-ping" />
                          <span className="absolute inline-flex h-[140%] w-[140%] rounded-full bg-red-300 opacity-30 animate-ping [animation-delay:300ms]" />
                        </>
                      )}

                      <div className="relative z-10 [&_svg]:text-white [&_svg]:stroke-white [&_*]:text-white [&_*]:stroke-white">
                        <SocketMicStreamer
                          ref={micRef}
                          normalButton={true}
                          isUserInputBar={true}
                          iconColor="#ffffff"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Autosave UX */}
                <div className="flex items-center gap-2 mt-2 h-4">
                  {isNotesSaving && (
                    <div className="flex items-center gap-1 text-[11px] text-blue-600 animate-pulse">
                      <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <span>{t("saving") || "Saving…"}</span>
                    </div>
                  )}

                  {isNotesSaving && !isSaving && (
                    <div className="flex items-center gap-1 text-[11px] text-green-600">
                      <span>✔</span>
                      <span>{t("saved") || "Saved"}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ROW 2 — Action Buttons */}
          <div className="flex justify-end gap-3 mt-4 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-white bg-red-500 hover:bg-red-600 rounded-md shadow transition-colors duration-200 font-medium"
            >
              {t("close")}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || isSaving || isGrievanceRegistering}
              className="bg-[#FC6C02] hover:[] disabled:bg-green-400 text-white text-sm font-medium px-6 py-2 rounded-md shadow transition-all duration-200 flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting...
                </>
              ) : (
                t("submit")
              )}
            </button>
          </div>
        </div>

        {isViewerOpen && (
          <DocumentViewerModal
            fileData={fileData}
            onClose={() => setIsViewerOpen(false)}
          />
        )}
      </div>
    </>,
    document.body
  );
};

VerifyDataModal.propTypes = {
  handleSubmitGrievance: PropTypes.func,
  botResponse: PropTypes.object,
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  mandatory_data_present: PropTypes.any,
  updated_grievance: PropTypes.string,
  field_form: PropTypes.object,
  category_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  org_code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  field_form_list: PropTypes.array,
  serverRemarks: PropTypes.string,
};

export default VerifyDataModal;
